import { useEffect, useRef, useState } from 'react';
import { subscribeSignalR, getSignalRConnection } from '../services/signalRConnection';

/**
 * Hook para receber notificações do SignalR Hub.
 * Isolado e seguro - não quebra o app em caso de erro.
 *
 * IMPORTANTE: a conexão é um SINGLETON compartilhado por aba
 * (services/signalRConnection.js). Este hook apenas registra o handler e observa
 * o estado. Múltiplos componentes usando o hook = 1 conexão só.
 *
 * O ciclo de vida da conexão NÃO depende da identidade de onNotificationReceived:
 * o handler vive num ref, então callbacks não-memoizados não derrubam/recriam nada
 * (o hook antigo recriava a conexão a cada mudança de referência do callback).
 */
export function useNotifications(onNotificationReceived) {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);

  // Sempre aponta para o callback mais recente sem religar a subscription
  const handlerRef = useRef(onNotificationReceived);
  useEffect(() => {
    handlerRef.current = onNotificationReceived;
  }, [onNotificationReceived]);

  useEffect(() => {
    const unsubscribe = subscribeSignalR({
      onNotification: (notification) => {
        if (handlerRef.current) {
          handlerRef.current(notification);
        }
      },
      onStateChange: ({ connected, error }) => {
        setIsConnected(connected);
        setConnectionError(error ?? null);
      },
    });

    return unsubscribe;
  }, []); // registra uma única vez por mount

  return {
    isConnected,
    connectionError,
    connection: getSignalRConnection(),
  };
}
